import React, { useMemo, useState, useEffect, useRef } from 'react';
import PkgSelect from './PkgSelect';

// Selettore posizione "a componenti": Area → Scaffale → Posizione → Piano, a cascata.
// I segmenti arrivano già scomposti da wsGetPositions (area, scaffale, posizione, piano)
// e la cella è la loro composizione "area-scaffale-posizione-piano" (es. "A-01-01-0").
// Le opzioni di ogni livello sono ricavate dalle posizioni reali filtrate dai livelli
// superiori → impossibile comporre una cella inesistente. In più un campo codice/scan
// accetta/mostra la cella completa e popola i 4 segmenti (fallback digitabile).
//
// API a "value stringa" come una <select> della cella: value = cella ('' se incompleta),
// onChange(cella, seg) riceve la cella composta quando i 4 segmenti risolvono, altrimenti ''.
// Il secondo argomento sono i segmenti grezzi {area,scaffale,posizione,piano}: serve a chi
// deve filtrare per colonna (Consultazione) invece di ricomporre la stringa.
//
// Con allowPartial il componente emette anche i prefissi parziali ('A', 'A-01') invece di
// aspettare i 4 segmenti: abilita la ricerca "tutta l'area / tutto lo scaffale". Default OFF,
// quindi Rientro/Posizionamento/Correzione (che scrivono in magazzino e vogliono una cella
// univoca) restano identici.

const byNum = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true });
const uniq = (arr) => [...new Set(arr)].sort(byNum);
const toOpts = (arr) => arr.map((x) => ({ value: x, label: x }));

const cellaOf = (s) =>
    [s.area, s.scaffale, s.posizione, s.piano].every(Boolean)
        ? `${s.area}-${s.scaffale}-${s.posizione}-${s.piano}`
        : '';

// Codice "parziale" dei soli segmenti compilati (per tenere il campo codice allineato
// alle tendine anche a selezione incompleta): {A,01} → "A-01".
const partialCode = (s) => {
    if (!s.area) return '';
    const parts = [s.area];
    if (!s.scaffale) return parts.join('-');
    parts.push(s.scaffale);
    if (!s.posizione) return parts.join('-');
    parts.push(s.posizione);
    if (s.piano) parts.push(s.piano);
    return parts.join('-');
};

// Completa i segmenti a valle quando hanno un'unica opzione (es. aree "piane" → scaffale 00)
// e scarta una scelta inferiore diventata non più valida dopo il cambio di un livello sup.
function resolveSegments(positions, a, s, p, pi) {
    if (!a) return { area: '', scaffale: '', posizione: '', piano: '' };
    const sc = uniq(positions.filter((x) => x.area === a).map((x) => x.scaffale));
    if (!s && sc.length === 1) s = sc[0];
    if (s && !sc.includes(s)) s = '';
    if (!s) return { area: a, scaffale: '', posizione: '', piano: '' };
    const po = uniq(positions.filter((x) => x.area === a && x.scaffale === s).map((x) => x.posizione));
    if (!p && po.length === 1) p = po[0];
    if (p && !po.includes(p)) p = '';
    if (!p) return { area: a, scaffale: s, posizione: '', piano: '' };
    const pn = uniq(positions.filter((x) => x.area === a && x.scaffale === s && x.posizione === p).map((x) => x.piano));
    if (!pi && pn.length === 1) pi = pn[0];
    if (pi && !pn.includes(pi)) pi = '';
    return { area: a, scaffale: s, posizione: p, piano: pi };
}

// Codice digitato → segmenti validati, con parsing progressivo: ogni livello è accettato solo
// se esiste davvero sotto i livelli superiori già risolti (quindi "A-99" non passa lo scaffale).
function parseCode(positions, raw) {
    const parts = String(raw).trim().toUpperCase().split('-');
    const areas = uniq(positions.map((x) => x.area));
    let a = '', s = '', p = '', pi = '';
    const matchArea = areas.find((x) => x.toUpperCase() === parts[0]);
    if (matchArea) a = matchArea;
    if (a && parts[1] !== undefined) {
        const sc = uniq(positions.filter((x) => x.area === a).map((x) => x.scaffale));
        if (sc.includes(parts[1])) s = parts[1];
    }
    if (a && s && parts[2] !== undefined) {
        const po = uniq(positions.filter((x) => x.area === a && x.scaffale === s).map((x) => x.posizione));
        if (po.includes(parts[2])) p = parts[2];
    }
    if (a && s && p && parts[3] !== undefined) {
        const pn = uniq(positions.filter((x) => x.area === a && x.scaffale === s && x.posizione === p).map((x) => x.piano));
        if (pn.includes(parts[3])) pi = parts[3];
    }
    return { area: a, scaffale: s, posizione: p, piano: pi };
}

const PositionPicker = ({ positions = [], value = '', onChange, allowPartial = false }) => {
    const [seg, setSeg] = useState({ area: '', scaffale: '', posizione: '', piano: '' });
    const [codeText, setCodeText] = useState('');

    // Ciò che il componente emette verso il padre: la cella completa, o il prefisso parziale.
    const codeOf = (s) => (allowPartial ? partialCode(s) : cellaOf(s));
    const emit = (resolved) => onChange(codeOf(resolved), resolved);

    // Il ref confronta il codice EMESSO (non la sola cella completa): in modalità parziale
    // cellaOf() resta '' finché i 4 segmenti non risolvono e la guardia non scatterebbe mai.
    const emittedRef = useRef('');
    emittedRef.current = codeOf(seg);

    // Sync dal value esterno (es. posizione suggerita dall'ordine): NON emette onChange.
    useEffect(() => {
        const v = (value || '').trim();
        if (v === emittedRef.current) return;
        const found = positions.find((x) => x.cella === v);
        if (found) {
            setSeg({ area: found.area, scaffale: found.scaffale, posizione: found.posizione, piano: found.piano });
            setCodeText(found.cella);
        } else if (allowPartial && v) {
            const parsed = parseCode(positions, v);
            setSeg(parsed);
            setCodeText(partialCode(parsed));
        } else if (!v) {
            setSeg({ area: '', scaffale: '', posizione: '', piano: '' });
            setCodeText('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, positions, allowPartial]);

    const areas = useMemo(() => uniq(positions.map((p) => p.area)), [positions]);
    const scaffali = useMemo(() => uniq(positions.filter((p) => p.area === seg.area).map((p) => p.scaffale)), [positions, seg.area]);
    const posizioni = useMemo(() => uniq(positions.filter((p) => p.area === seg.area && p.scaffale === seg.scaffale).map((p) => p.posizione)), [positions, seg.area, seg.scaffale]);
    const piani = useMemo(() => uniq(positions.filter((p) => p.area === seg.area && p.scaffale === seg.scaffale && p.posizione === seg.posizione).map((p) => p.piano)), [positions, seg.area, seg.scaffale, seg.posizione]);

    // Tendine → codice: applica la scelta (auto-completa i livelli a opzione unica),
    // allinea il campo codice al parziale ed emette la cella (completa o '').
    const apply = (next) => {
        const resolved = resolveSegments(positions, next.area, next.scaffale, next.posizione, next.piano);
        setSeg(resolved);
        setCodeText(partialCode(resolved));
        emit(resolved);
    };

    // Codice → tendine: parsing progressivo e validato del testo digitato → aggiorna le
    // tendine live (senza toccare il testo, così non si combatte con la digitazione).
    const onCodeChange = (raw) => {
        setCodeText(raw);
        const resolved = parseCode(positions, raw);
        setSeg(resolved);
        emit(resolved);
    };

    return (
        <div className="pos-picker">
            <div className="pos-picker-row">
                <div className="pos-picker-seg">
                    {/* In ricerca l'Area è azzerabile: serve per tornare da "area A" a "tutto il magazzino". */}
                    <PkgSelect value={seg.area} options={toOpts(areas)} onChange={(v) => apply({ area: v })}
                        isSearchable={false} isClearable={allowPartial} placeholder="Area" />
                </div>
                <div className="pos-picker-seg">
                    <PkgSelect value={seg.scaffale} options={toOpts(scaffali)} onChange={(v) => apply({ area: seg.area, scaffale: v })}
                        isDisabled={!seg.area} isSearchable={false} isClearable={false} placeholder="Scaff." />
                </div>
                <div className="pos-picker-seg">
                    <PkgSelect value={seg.posizione} options={toOpts(posizioni)} onChange={(v) => apply({ area: seg.area, scaffale: seg.scaffale, posizione: v })}
                        isDisabled={!seg.scaffale} isSearchable={false} isClearable={false} placeholder="Pos." />
                </div>
                <div className="pos-picker-seg">
                    <PkgSelect value={seg.piano} options={toOpts(piani)} onChange={(v) => apply({ area: seg.area, scaffale: seg.scaffale, posizione: seg.posizione, piano: v })}
                        isDisabled={!seg.posizione} isSearchable={false} isClearable={false} placeholder="Piano" />
                </div>
            </div>
            <input
                className="pos-picker-code"
                value={codeText}
                onChange={(e) => onCodeChange(e.target.value)}
                placeholder="oppure scrivi il codice cella (es. A-01-01-0)"
            />
        </div>
    );
};

export default PositionPicker;
